"use client";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import Image from "next/image";
import Link from "next/link";
import {
  BlocksRenderer,
  type BlocksContent,
} from "@strapi/blocks-react-renderer";
import { useMemo, useEffect, useRef, useState } from "react";
import { absoluteCmsUrl } from "../base-image/BaseImage";
import { Heading, type HeadingProps } from "@repo/ui/heading";

type StrapiImageFormat = {
  url?: string | null;
  width?: number;
  height?: number;
  ext?: string;
  mime?: string;
  name?: string;
  size?: number;
};
type StrapiMedia = {
  id: number;
  url?: string | null;
  width?: number;
  height?: number;
  name?: string;
  alternativeText?: string | null;
  caption?: string | null;
  formats?: {
    large?: StrapiImageFormat;
    medium?: StrapiImageFormat;
    small?: StrapiImageFormat;
    thumbnail?: StrapiImageFormat;
  };
};
type SliderItem = {
  id: number;
  title: string;
  subtitle?: string | null;
  description?: BlocksContent | null;
  url?: string | null;
  image?: StrapiMedia | null;
};

interface Headline {
  heading: HeadingProps; // { title, level }
  description?: BlocksContent | null; // blocks array or null
}

export interface ItemsSliderProps {
  headline: Headline;
  items: SliderItem[]; // from response.items
}

type LogoConfig = {
  src: string;
  width: number;
  height: number;
  sizes?: string;
  className?: string;
};

const HOTEL_LOGOS: Record<string, LogoConfig> = {
  dottingen: {
    src: "/assets/dottingen-white-logo.svg",
    width: 152,
    height: 21,
    sizes: "(max-width:768px) 120px, (max-width:1024px) 160px, 180px",
  },
  littau: {
    src: "/assets/littau-logo.svg",
    width: 87,
    height: 22,
    sizes: "(max-width:768px) 110px, (max-width:1024px) 130px, 140px",
    className: "invert",
  },
};

const normalizeLocation = (v?: string | null) => {
  if (!v) return "";
  return v
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
};

const getHotelLogo = (location?: string | null): LogoConfig | null => {
  const key = normalizeLocation(location);
  return HOTEL_LOGOS[key] ?? null;
};

const toId = (title?: string | null) => {
  if (!title) return undefined;
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

const isNonEmpty = (v?: string | null): v is string =>
  typeof v === "string" && v.trim().length > 0;

function pickBestImageUrl(img?: StrapiMedia | null): string | null {
  if (!img) return null;
  const candidate =
    img.url ??
    img.formats?.large?.url ??
    img.formats?.medium?.url ??
    img.formats?.small?.url ??
    null;
  if (!isNonEmpty(candidate)) return null;
  const abs = absoluteCmsUrl(candidate);
  return isNonEmpty(abs) ? abs : null;
}

export default function ItemsSlider({ headline, items }: ItemsSliderProps) {
  const slides = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  const [sliderRef] = useKeenSlider<HTMLDivElement>({
    slides: { perView: 1.2, spacing: 16 },
    breakpoints: {
      "(min-width: 768px)": { slides: { perView: 2.2, spacing: 24 } },
      "(min-width: 1024px)": { slides: { perView: 2.8, spacing: 24 } },
      "(min-width: 1280px)": { slides: { perView: 3, spacing: 24 } },
    },
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    if (!rootRef.current || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries.length > 0 ? entries[0] : undefined;
        if (entry && entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { root: null, rootMargin: "200px 0px", threshold: 0.01 },
    );
    observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  if (slides.length === 0) return null;

  const sectionId = toId(headline.heading.title);

  return (
    <>
      <div className="max-w-7xl mx-auto text-center reveal-stagger flex flex-col items-center gap-6 px-6 md:pt-32 md:pb-14 pb-6">
        <Heading
          level={headline.heading.level}
          className="xl:text-7xl lg:text-6xl sm:text-5xl text-4xl tracking-tighter reveal-item"
          id={sectionId}
        >
          {headline.heading.title}
        </Heading>
        {headline.description && (
          <div className="max-w-xl text-black reveal-item">
            <BlocksRenderer content={headline.description} />
          </div>
        )}
      </div>

      <div
        className="max-w-screen-2xl w-full mx-auto reveal-item"
        ref={rootRef}
      >
        {isVisible ? (
          <div ref={sliderRef} className="keen-slider pb-14">
            {slides.map((item, idx) => {
              const imgUrl = pickBestImageUrl(item.image);
              const alt =
                item.image?.alternativeText ||
                item.image?.caption ||
                item.image?.name ||
                item.title;

              const location = item.subtitle || undefined;
              const logo = getHotelLogo(location);

              const slideClass =
                "flex flex-col justify-between p-6 xl:h-[800px] h-[600px] relative z-10";

              const content = (
                <>
                  <div className="opacity-40 bg-gradient-to-t from-black to-transparent top-0 left-0 absolute w-full h-full -z-10" />
                  <div className="opacity-20 bg-gradient-to-b from-black to-transparent top-0 left-0 absolute w-full h-full -z-10" />

                  {imgUrl ? (
                    <Image
                      className="object-cover object-center w-full h-full absolute top-0 left-0 -z-20"
                      width={573}
                      height={700}
                      alt={alt}
                      src={imgUrl}
                      loading="lazy"
                      sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 33vw"
                    />
                  ) : (
                    <div
                      className="absolute top-0 left-0 w-full h-full -z-20 bg-neutral-200"
                      aria-hidden
                    />
                  )}

                  <div className="text-white flex items-start gap-3">
                    {logo ? (
                      <Image
                        src={logo.src}
                        alt={`${location} logo`}
                        width={logo.width}
                        height={logo.height}
                        loading="lazy"
                        className={logo.className ?? "object-contain"}
                        sizes={
                          logo.sizes ??
                          "(max-width:768px) 120px, (max-width:1024px) 160px, 180px"
                        }
                      />
                    ) : (
                      location && (
                        <p className="md:text-3xl text-2xl">{location}</p>
                      )
                    )}
                  </div>

                  <div className="text-white md:pr-34 pr-20 flex flex-col gap-1.5">
                    <h3 className="md:text-3xl text-2xl tracking-tighter">
                      {item.title}
                    </h3>
                    {Array.isArray(item.description) && (
                      <div className="[&_*]:!my-0">
                        <BlocksRenderer content={item.description} />
                      </div>
                    )}
                  </div>
                </>
              );

              return item.url ? (
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  key={item.id}
                  href={item.url}
                  className={`keen-slider__slide`}
                >
                  <div className={slideClass}>{content}</div>
                </a>
              ) : (
                <div
                  key={item.id}
                  className={`keen-slider__slide ${slideClass}`}
                >
                  {content}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 pb-14 px-6">
            {slides.slice(0, 6).map((item) => {
              const imgUrl = pickBestImageUrl(item.image);
              const alt =
                item.image?.alternativeText ||
                item.image?.caption ||
                item.image?.name ||
                item.title;
              return (
                <div
                  key={item.id}
                  className="relative h-[320px] rounded-xl overflow-hidden"
                >
                  {imgUrl ? (
                    <Image
                      className="object-cover object-center w-full h-full"
                      width={600}
                      height={400}
                      alt={alt}
                      src={imgUrl}
                      loading="lazy"
                      sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 33vw"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
